import { LDP_DOMAIN, LDP_SUBDOMAINS } from "@/config/constants";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { BiPlanet, BiRocket } from "react-icons/bi";
import DomainConfigModal, { CustomizeDomainForm } from "./DomainConfigModal";
import DomainOption from "./DomainOption";
import { useLocation } from "react-router-dom";
import { CreateLDPStateLocation } from "../CreateLDPWithStepper";
import UserRepository from "@/utilities/repositories/Users";
import useProfile from "@/hooks/use-profile";
import { Roles } from "@/utilities/types/Users";
import { E_LANDING_PAGE_TYPE } from "@/utilities/types/LandingPage";

const DomainCustomization = ({
  projectId,
  domainInitialValues,
  onSave,
}: {
  projectId: string;
  domainInitialValues: CustomizeDomainForm;
  onSave?: (values: CustomizeDomainForm) => Promise<void>;
}) => {
  const [selectedOption, setSelectedOption] =
    useState<E_LANDING_PAGE_TYPE | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isInactiveUser, setIsInactiveUser] = useState(false);

  const { profile } = useProfile();

  const isAdmin = profile?.role === Roles.ADMIN;
  const handleCustomDomain = () => {
    setSelectedOption(E_LANDING_PAGE_TYPE.CUSTOM);
    setIsModalOpen(true);
  };

  const handleSubdomain = () => {
    setSelectedOption(E_LANDING_PAGE_TYPE.SUBDOMAIN);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedOption(null);
  };

  const form = useFormContext();

  const handleSubmit = async (values: CustomizeDomainForm) => {
    setIsModalOpen(false);

    form.setValue("domain", values);

    await onSave?.(values);
  };

  const locationState: CreateLDPStateLocation = useLocation()?.state;

  const userId = locationState?.userId;

  useEffect(() => {
    if (userId && isAdmin) {
      // Admin need to select user to assign domain otherwise use current user
      UserRepository.getUserById(userId).then((res) => {
        if (res.data?.statusAccount === "inactive") {
          setIsInactiveUser(true);
        }
      });
    }
  }, [userId, isAdmin]);

  return (
    <div className="max-w-3xl mx-auto p-6">
      <h1 className="text-2xl font-semibold text-center mb-4">
        How to Publish Your Page?
      </h1>

      <div className="text-center text-gray-500 mb-8">
        SECURELY HOSTED BY US
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        <DomainOption
          icon={BiPlanet}
          title="Custom Domain"
          description="Use your own domain to deploy your website"
          onChoose={handleCustomDomain}
        />

        <DomainOption
          icon={BiRocket}
          title="Subdomain"
          description={`Use a free subdomain using one of our <strong>provided domain</strong> names`}
          onChoose={handleSubdomain}
        />
      </div>

      {isModalOpen && (
        <DomainConfigModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          type={selectedOption || E_LANDING_PAGE_TYPE.SUBDOMAIN}
          onSubmit={handleSubmit}
          suffixDomains={LDP_SUBDOMAINS}
          defaultSuffixDomain={LDP_SUBDOMAINS[0]}
          projectId={projectId}
          initialValues={domainInitialValues}
          isInactiveUser={isInactiveUser}
        />
      )}
    </div>
  );
};

export default DomainCustomization;
